<template>
  <div v-if="shouldShow" class="binds-zoomable-image-container">
    <div class="bind-image-zoom-container" :class="containerClass">
      <div class="binds-image-wrapper" :class="{ 'binds-has-background': hasBackground }">
        <img
          :src="imageUrl"
          :alt="imageAlt"
          class="binds-zoomable-image"
          :class="imageClass"
        />
        <div class="binds-zoom-indicator" @click.stop="openImageModal">
          <i class="material-icons">zoom_in</i>
        </div>
        <slot name="overlay"></slot>
      </div>
      <div v-if="showLabel" class="binds-option-label">
        {{ imageAlt }}
      </div>
    </div>

    <!-- Image Modal -->
    <div v-if="showModal" class="binds-image-modal" @click="closeModal">
      <div class="binds-modal-content" @click.stop>
        <div class="binds-modal-image-container">
          <img :src="imageUrl" :alt="imageAlt" class="binds-modal-image">
          <div class="binds-modal-close" @click="closeModal">
            <i class="material-icons">close</i>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ZoomableImage',
  props: {
    image: {
      type: [String, Object],
      required: true
    },
    containerClass: {
      type: String,
      default: ''
    },
    imageClass: {
      type: String,
      default: ''
    },
    showLabel: {
      type: Boolean,
      default: false
    },
    hasBackground: {
      type: Boolean,
      default: true
    },
    questionType: {
      type: String,
      default: ''
    },
    showImageOnTitle: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      showModal: false
    }
  },
  computed: {
    shouldShow () {
      // If image is a string URL, show it
      if (typeof this.image === 'string') {
        return true
      }

      // If image is an object, check if it's not hidden and has required properties
      if (this.image && typeof this.image === 'object') {
        // Don't show if explicitly hidden
        if (this.image.isHidden) {
          return false
        }

        // Check for required properties
        if (!this.image.label || !this.image.value) {
          return false
        }

        // For CSAT questions, check if showImageOnTitle is enabled
        if (this.questionType === 'csat') {
          return this.showImageOnTitle
        }

        return true
      }

      return false
    },
    imageUrl () {
      return typeof this.image === 'string' ? this.image : this.image.label
    },
    imageAlt () {
      return typeof this.image === 'string' ? '' : this.image.value
    }
  },
  methods: {
    openImageModal () {
      this.showModal = true
      document.body.style.overflow = 'hidden'
    },
    closeModal () {
      this.showModal = false
      document.body.style.overflow = 'auto'
    }
  }
}
</script>

<style lang="scss">
.binds-zoomable-image-container {
  width: 100%;
  height: 100%;
  .bind-image-zoom-container {
    position: relative;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;

    .binds-image-wrapper {
      position: relative;
      width: 100%;
      padding-top: 100%;
      border-radius: 8px;
      overflow: hidden;
      transition: all 0.2s ease;

      &.binds-has-background {
        background-color: #f8f8f8;

        &::before {
          content: '';
          position: absolute;
          inset: 0;
          background: #f8f8f8;
          z-index: 1;
        }
      }

      .binds-zoomable-image {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        z-index: 2;
        transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      }

      .binds-zoom-indicator {
        position: absolute;
        top: 8px;
        right: 8px;
        background: rgba(255, 255, 255, 0.9);
        border-radius: 50%;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0;
        transition: opacity 0.2s ease, transform 0.2s ease;
        cursor: zoom-in;
        z-index: 4;

        @media (hover: none) {
          opacity: 0.7;
          background: rgba(255, 255, 255, 0.8);
        }

        i {
          font-size: 20px;
          color: #666;
        }

        &:hover {
          transform: scale(1.1);
        }
      }

      @media (hover: hover) {
        &:hover {
          .binds-zoom-indicator {
            opacity: 1;
          }

          .binds-zoomable-image {
            transform: scale(1.02);
          }
        }
      }
    }

    .binds-option-label {
      margin-top: 12px;
      text-align: center;
      font-size: 0.95em;
      font-weight: 500;
      width: 100%;
      padding: 0 5px;
      word-break: break-word;
    }
  }
}

.binds-image-modal {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.87);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
  animation: fadeIn 0.15s ease-out;
  border-radius: 5px;
  .binds-modal-content {
    position: relative;
    width: auto;
    height: auto;
    max-width: 70%;
    max-height: 70%;
    display: flex;
    align-items: center;
    justify-content: center;
    animation: zoomIn 0.15s ease-out;
    will-change: transform, opacity;

    .binds-modal-image-container {
      position: relative;
      width: auto;
      height: auto;
      display: flex;
      align-items: center;
      justify-content: center;

      .binds-modal-image {
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
        border-radius: 4px;
      }

      .binds-modal-close {
        position: absolute;
        top: -40px;
        right: -40px;
        width: 32px;
        height: 32px;
        background: rgba(255, 255, 255, 0.98);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.2s ease-out;
        z-index: 10000;

        i {
          font-size: 20px;
          color: #333;
        }

        &:hover {
          background: #fff;
          transform: scale(1.1);
        }
      }
    }
  }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes zoomIn {
  from {
    opacity: 0;
    transform: scale(0.98);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@media only screen and (max-width: 600px) {
  .binds-image-modal {
    padding: 15px;

    .binds-modal-content {
      max-width: 85%;
      max-height: 85%;

      .binds-modal-image-container {
        .binds-modal-close {
          top: -15px;
          right: -15px;
          width: 28px;
          height: 28px;

          i {
            font-size: 18px;
          }
        }
      }
    }
  }
}
</style>
